import {
	AvailableBindersAndWarrantiesForConstructionSite,
	PAGES,
	SanitizedWarrantySelection,
} from "@dune/types";
import { Button, HorizontalStepper, Loader } from "@dune/ui";
import { ArrowLeft, ArrowRight } from "@untitled-ui/icons-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useUpdateConstructionSiteOffers } from "../../api/mutations/constructionSite.mutations";
import { useGetConstructionSiteBindersWarranties } from "../../api/queries/constructionSite.queries";
import { useRequiredParams } from "../../hooks/useRequiredParams";
import ConstructionSiteBindersErrors from "./ConstructionSiteBindersErrors";
import { BinderWarrantyOfferContainerWithOptionsAndPricingProvider } from "./offers/BinderWarrantyOfferContainerWithOptionsAndPricingProvider";
import { navigateBack } from "../../utils/navigation";

const ConstructionSiteBinders = () => {
	const navigate = useNavigate();
	const { id } = useRequiredParams<{ id: string }>();

	const [binderWarranties, setBinderWarranties] = useState<
		SanitizedWarrantySelection[]
	>([]);
	const { data: constructionSiteBindersWarranties, isFetching } =
		useGetConstructionSiteBindersWarranties(Number(id));

	const { mutate, isPending: isGeneratingDocuments } =
		useUpdateConstructionSiteOffers(id);

	const { availableBindersAndWarranties, allFailures } =
		constructionSiteBindersWarranties || {};

	useEffect(() => {
		if (Array.isArray(availableBindersAndWarranties)) {
			setBinderWarranties(() => []);
			availableBindersAndWarranties?.map(
				(
					binderWarranties: AvailableBindersAndWarrantiesForConstructionSite,
				) => {
					if (binderWarranties?.warranties) {
						const { warranties } = binderWarranties;
						setBinderWarranties((bindersWarranties) => [
							...bindersWarranties,
							...warranties,
						]);
					}
				},
			);
		}
	}, [availableBindersAndWarranties]);

	const onSubmit = async (withDocumentsGeneration = false) => {
		if (
			binderWarranties.length > 0 &&
			binderWarranties.every((binderWarranty) => !binderWarranty.checked)
		) {
			toast.error("Veuillez sélectionner au moins une garantie");
			return;
		}
		mutate({ id, binderWarranties, withDocumentsGeneration });
	};

	const handleBinderWarrantyCheckboxChange = (id: number) => {
		setBinderWarranties((binderWarranties: SanitizedWarrantySelection[]) =>
			binderWarranties.map((binderWarranty) =>
				binderWarranty.id === id
					? { ...binderWarranty, checked: !binderWarranty.checked }
					: binderWarranty,
			),
		);
	};

	const handleDependentWarrantyCheckboxChange = (
		baseWarrantyId: number,
		id: number,
	) => {
		setBinderWarranties((binderWarranties: SanitizedWarrantySelection[]) =>
			binderWarranties.map((binderWarranty) =>
				binderWarranty.id === baseWarrantyId
					? {
							...binderWarranty,
							dependentWarranties:
								binderWarranty.dependentWarranties?.map((dependentWarranty) =>
									dependentWarranty.id === id
										? {
												...dependentWarranty,
												checked: !dependentWarranty.checked,
											}
										: dependentWarranty,
								) ?? [],
						}
					: binderWarranty,
			),
		);
	};

	const handleBinderWarrantyWarrantyCheckboxChange = (
		baseWarrantyId: number,
		id: number,
	) => {
		setBinderWarranties((binderWarranties: SanitizedWarrantySelection[]) =>
			binderWarranties.map((binderWarranty) =>
				binderWarranty.id === baseWarrantyId
					? {
							...binderWarranty,
							warranties: binderWarranty.warranties.map((warranty) =>
								warranty.id === id
									? { ...warranty, checked: !warranty.checked }
									: warranty,
							),
						}
					: binderWarranty,
			),
		);
	};

	const handleBinderWarrantyWarrantyLimitChange = (
		baseWarrantyId: number,
		warrantyId: number,
		limitOptionId: number,
	) => {
		setBinderWarranties((binderWarranties: SanitizedWarrantySelection[]) =>
			binderWarranties.map((binderWarranty) =>
				binderWarranty.id === baseWarrantyId
					? {
							...binderWarranty,
							warranties: binderWarranty.warranties.map((warranty) =>
								warranty.id === warrantyId
									? {
											...warranty,
											selectedLimitOptionId: limitOptionId,
										}
									: warranty,
							),
						}
					: binderWarranty,
			),
		);
	};

	const handleBrokerFeesChange = (
		baseWarrantyId: number,
		brokerFees: number,
	) => {
		setBinderWarranties((binderWarranties: SanitizedWarrantySelection[]) =>
			binderWarranties.map((binderWarranty) =>
				binderWarranty.id === baseWarrantyId
					? { ...binderWarranty, brokerFees }
					: binderWarranty,
			),
		);
	};

	return (
		<div className="flex flex-col w-full items-center justify-center">
			<div className="w-full px-6 pt-6 pb-4 border-bottom border-solid border-1 border-gray-100">
				<Button
					className="self-start text-[#144C65] px-0 has-[>svg]:px-0"
					variant="link"
					size="sm"
					onClick={() =>
						navigateBack({
							navigate,
							fallbackUrl: PAGES.PROJECTS.index,
						})
					}
				>
					<ArrowLeft /> Retour
				</Button>
				<h1 className="text-[#144C65] text-[32px] font-medium">
					Nouveau projet - Chantier de construction
				</h1>
			</div>
			<div className="w-full">
				<HorizontalStepper
					steps={[
						{ step: 1, title: "Mon client" },
						{ step: 2, title: "Opération à assurer" },
						{ step: 3, title: "Indications tarifaires" },
					]}
					currentStepIndex={3}
					setCurrentStepIndex={(step) => {
						navigate(PAGES.PROJECTS.edit.withParams(id, step));
					}}
					className="px-6"
				/>
			</div>
			{isFetching ? (
				<div className="flex flex-col justify-center p-6 w-full items-center">
					<Loader />
				</div>
			) : (
				<div className="flex flex-col justify-center p-6 w-full gap-8">
					{binderWarranties?.filter(
						(binderWarranty) => binderWarranty.isAvailable,
					).length > 0 && (
						<div className="flex-3 flex-col items-center justify-center">
							<div className="flex flex-col gap-6 w-full">
								<div className="flex flex-col gap-1 w-full">
									<h1 className="text-[#144C65] text-2xl font-medium">
										Voici les offres que DUNE peut vous proposer sur la base des
										informations transmises
									</h1>
									<h4>
										Sélectionnez une offre pour voir le détail et obtenir les
										indications tarifaires
									</h4>
								</div>
								<div className="flex flex-row gap-6 w-full overflow-x-auto">
									{binderWarranties
										.filter((binderWarranty) => binderWarranty.isAvailable)
										.map((binderWarranty: SanitizedWarrantySelection) => {
											return (
												binderWarranty.isAvailable && (
													<BinderWarrantyOfferContainerWithOptionsAndPricingProvider
														binderWarranty={binderWarranty}
														constructionSiteId={id}
														isDisabled={isGeneratingDocuments}
														handleBinderWarrantyCheckboxChange={
															handleBinderWarrantyCheckboxChange
														}
														handleDependentWarrantyCheckboxChange={
															handleDependentWarrantyCheckboxChange
														}
														handleBinderWarrantyWarrantyCheckboxChange={
															handleBinderWarrantyWarrantyCheckboxChange
														}
														handleBinderWarrantyWarrantyLimitChange={
															handleBinderWarrantyWarrantyLimitChange
														}
														handleBrokerFeesChange={handleBrokerFeesChange}
													/>
												)
											);
										})}
								</div>
							</div>
						</div>
					)}
					<div className="flex flex-row justify-between">
						<Button
							className="self-end"
							type="button"
							variant="outline"
							size="lg"
							onClick={() => {
								navigate(PAGES.PROJECTS.edit.withParams(id, 2));
							}}
							disabled={isGeneratingDocuments}
						>
							<ArrowLeft className="w-4 h-4" />
							Précédent
						</Button>
						<div className="flex flex-row gap-4">
							<Button
								className="self-end"
								type="button"
								variant="outlineBlue"
								size="lg"
								onClick={() => onSubmit()}
								disabled={isGeneratingDocuments}
							>
								Enregistrer et fermer
							</Button>
							<Button
								className="self-end"
								type="button"
								variant="default"
								size="lg"
								onClick={() => onSubmit(true)}
								isLoading={isGeneratingDocuments}
							>
								Obtenir mes indications tarifaires
								<ArrowRight className="w-4 h-4" />
							</Button>
						</div>
					</div>
					{allFailures && (
						<ConstructionSiteBindersErrors failures={allFailures} />
					)}
				</div>
			)}
		</div>
	);
};

export default ConstructionSiteBinders;
